
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { statsModule, commonModule, installationsModule, actionTypesModule, companyModule } from "@/store/modules/store-accessor";
import dayjs from "dayjs";
import KeycloakSession from '@/helpers/keycloak-session'

@Component
export default class Sidebar extends Vue {

  @Prop()
  source: string;

  quickAddMenu = false;

  get kcSession() {
    return new KeycloakSession();
  }

  get quickAddLoader () {
    return this.quickAddBtnLoading || this.quickAddTaskLoading || this.quickAddCompanyExpenseLoading || this.quickAddJobLoading || this.quickAddInstallationLoading || this.quickAddOfferLoading;
  }

  sidebarWidth =  240
  sidebarMinWidth = 96
  quickAddBtnLoading = false;
  quickAddTaskLoading = false;
  quickAddCompanyExpenseLoading = false;
  quickAddJobLoading = false;
  quickAddOfferLoading = false;
  quickAddInstallationLoading = false;


  get items() {
    const items = [
      {
        title: "dashboard",
        icon: "mdi-home",
        link: "/dashboard",
        permissions: ["admin", "editor"],
      },
      {
        title: "clients",
        icon: "mdi-contacts-outline",
        link: "/clients",
        permissions: ["admin", "editor"],
      },
      {
        title: "installations",
        icon: "mdi-home-city-outline",
        link: "/installations",
        permissions: ["admin","editor"],
      },
      {
        title: "appointments",
        icon: "mdi-clipboard-list-outline",
        link: "/appointments",
        permissions: ["admin","editor"],
      },
      {
        title: "jobs",
        icon: "mdi-tools",
        link: "/jobs",
        permissions: ["admin","editor"],
      },
      {
        title: "tasks",
        icon: "mdi-format-list-numbered-rtl",
        link: "/tasks",
        permissions: ["admin","editor"],
      },
      {
        title: "items",
        icon: "mdi-water-boiler",
        link: "/items",
        permissions: ["admin","editor"],
      },
      {
        title: "map",
        icon: "mdi-google-maps",
        link: "/map",
        permissions: ["admin","editor"],
      },
      {
        title: "calendar",
        icon: "mdi-calendar",
        link: "/calendar",
        permissions: ["admin","editor"],
      },
      {
        title: "myCompany",
        icon: "mdi-office-building-cog-outline",
        link: "/my-company",
        permissions: ["admin","editor"],
      }
    ];

    if (this.companyHasAdvancedPlan) {
      items.splice(3, 0, {
        title: "offers",
        icon: "mdi-file-multiple",
        link: "/offers",
        permissions: ["admin", "editor"],
      });
    }

    return items;
  }

  hasAccess(permissions: []) {
    return permissions.some(el => this.kcSession.userHasRole(el));
  }

  get DRAWER_STATE() {
    return statsModule.drawerState;
  }

  get companyHasAdvancedPlan() {
    return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
  }

  get advancedModuleEntitlement(): ModuleEntitlementDto | undefined {
    return companyModule.company?.module_entitlements?.find(
      (entitlement) => entitlement.module_code === 'DFB_ADVANCED_PLAN'
    );
  }

  get advancedModuleDaysRemaining(): number | null {
    if (!this.companyHasAdvancedPlan || !this.advancedModuleEntitlement?.ends_at) {
      return null;
    }

    const days = dayjs(this.advancedModuleEntitlement.ends_at)
      .endOf('day')
      .diff(dayjs().startOf('day'), 'day');

    return Math.max(0, days);
  }

  get advancedModuleMonthsRemaining(): number {
    if (!this.advancedModuleEntitlement?.ends_at) {
      return 0;
    }

    return dayjs(this.advancedModuleEntitlement.ends_at)
      .endOf('day')
      .diff(dayjs().startOf('day'), 'month');
  }

  get advancedModuleRemainingText(): string {
    const days = this.advancedModuleDaysRemaining;
    if (days === null) {
      return '';
    }

    const months = this.advancedModuleMonthsRemaining;

    if (months > 2) {
      return '';
    }

    if (months >= 1) {
      if (months === 1) {
        return this.$t('advancedModuleMonthRemaining') as string;
      }

      return this.$t('advancedModuleMonthsRemaining', { months }) as string;
    }

    if (days === 1) {
      return this.$t('advancedTrialDayRemaining') as string;
    }

    return this.$t('advancedTrialDaysRemaining', { days }) as string;
  }

  get advancedModuleRemainingIsUrgent(): boolean {
    const days = this.advancedModuleDaysRemaining;
    if (days === null || this.advancedModuleMonthsRemaining >= 1) {
      return false;
    }

    return days < 15;
  }

  get showPlanSidebarFooter() {
    return !this.DRAWER_STATE || this.companyHasAdvancedPlan;
  }

  get planPricingUrl(): string {
    const lang = localStorage.getItem('lang') || 'el';

    if (lang === 'el') {
      return 'https://domx.io/el/domx-for-business-el#pricing';
    }

    return 'https://domx.io/domx-for-business#pricing';
  }

  async quickAdd (){
    this.quickAddBtnLoading = true;
    await installationsModule.getInstallationNextTagId();
    commonModule.getDurationCombo();
    this.quickAddBtnLoading = false;
    commonModule.showModal({ name: 'quick-add-appointment-modal' })
  }

  async quickAddTask () {
    this.quickAddTaskLoading = true;
    await actionTypesModule.getActionTypesCombo();
    commonModule.showModal({ name: 'quick-add-task-modal' });
    this.quickAddTaskLoading = false;
  }

  async quickAddCompanyExpense () {
    this.quickAddCompanyExpenseLoading = true;
    await commonModule.getPaymentTypeCombo();
    commonModule.showModal({ name: 'add-company-expense-modal' });
    this.quickAddCompanyExpenseLoading = false;
  }

  async quickAddJob () {
    this.quickAddJobLoading = true;
    commonModule.showModal({ name: 'quick-add-job-modal' });
    this.quickAddJobLoading = false;
  }

  async quickAddOffer () {
    this.quickAddOfferLoading = true;
    commonModule.showModal({ name: 'quick-add-offer-modal' });
    this.quickAddOfferLoading = false;
  }

  quickAddPayments () {
    commonModule.showModal({ name: 'quick-add-payments-modal' });
  }

  async quickAddInstallation () {
    this.quickAddInstallationLoading = true;
    commonModule.showModal({ name: 'quick-add-installation-modal', payload: { justInstalltion: true } });
    this.quickAddInstallationLoading = false;
  }

}

